import MyCardsCollection from '../../components/MyCardsCollection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './WantList.css';
import { SortOrder } from 'types/interfaces/sort';
import { useSortPreference } from 'hooks/useSortPreference';
import {
  useCardToWantListQuantityUpdateMutation,
  useRemoveCardFromWantListMutation,
  useWantListFetchPaginatedQuery,
  useAddFromPrintingsMutation,
  useAddSimpleMutation,
  useDeleteAllWantListItemsMutation,
  useBatchUpdateWantListMutation,
} from 'store/services/collectionList/wantList/wantListApi';
import { useGetWantListDefaultsQuery } from 'store/services/settings/settingsApi';
import { ConsolidatedCard } from 'store/services/cards/search/cardsSearchApi';
import DeleteWantListModal from 'components/DeleteWantListModal/DeleteWantListModal';
import { toast } from 'react-toastify';
import { mapFrontendConditionsToBackend } from 'utils/wantListDefaultsMapper';
import { mapFrontendToBackend as mapFrontendLanguagesToBackend } from 'constants/languages';

const WANT_LIST_ADD_CARDS_PLACEHOLDER =
  'Card name';

export const WantList = () => {
  const { orderBy, setOrderBy } = useSortPreference('wantListSort', 'created_at', 'desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'comfortable' | 'compact'>(() => {
    // Load saved view mode from localStorage
    const saved = localStorage.getItem('wantListViewMode');
    return (saved as 'list' | 'comfortable' | 'compact') || 'list';
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination when search changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  // Clear search on unmount (tab switch)
  useEffect(() => {
    return () => {
      setSearchTerm('');
      setIsSearchOpen(false);
    };
  }, []);

  const {
    data: wantListData,
    isFetching: wantListLoading,
  } = useWantListFetchPaginatedQuery({
    page: currentPage,
    ...orderBy,
    search: debouncedSearchTerm || undefined, // Don't send empty string
  });
  const [addFromPrintings] = useAddFromPrintingsMutation();
  const [addSimple] = useAddSimpleMutation();
  const [removeCard] = useRemoveCardFromWantListMutation();
  const [wantListCardQuantityChange] =
    useCardToWantListQuantityUpdateMutation();
  const [deleteAllWantListItems, { isLoading: isDeleting }] = useDeleteAllWantListItemsMutation();
  const [batchUpdate] = useBatchUpdateWantListMutation();
  const { data: wantListDefaults } = useGetWantListDefaultsQuery();

  const allWantListCards = useMemo(
    () => wantListData?.wantList || [],
    [wantListData?.wantList]
  );

  const onCardAdd = async (identifier: string | number, cardData?: any, searchMode?: 'any' | 'specific') => {
    console.log('WantList onCardAdd called with:', { identifier, cardData, searchMode });
    
    if (typeof identifier === 'string' && identifier) {
      try {
        if (searchMode === 'any') {
          // Handle consolidated card addition using add-simple endpoint
          const consolidatedCard = cardData as ConsolidatedCard;
          console.log('Adding card in Any Set mode:', consolidatedCard);

          const requestPayload = {
            cardName: consolidatedCard?.cardName || 'Unknown Card',
            oracleId: identifier,
            quantity: 1, // Default to adding 1 copy
            anySet: true, // Explicitly set to true for "Any Set" mode
            acceptableLanguages: wantListDefaults?.defaultLanguages
              ? mapFrontendLanguagesToBackend(wantListDefaults.defaultLanguages)
              : ['en'], // Fallback to English (lowercase for backend)
            acceptFoil: wantListDefaults?.defaultAcceptFoil ?? true,
            acceptNonFoil: wantListDefaults?.defaultAcceptNonFoil ?? true,
          };

          console.log('Sending add-simple request:', requestPayload);
          await addSimple(requestPayload).unwrap();
          console.log('Card added successfully with add-simple (Any Set mode)');
        } else {
          // Handle specific card addition (existing logic)
          console.log('Adding card with oracleId:', identifier);
          console.log('Card set info:', { setName: cardData?.setName, setId: cardData?.setId });

          const hasSpecificId = cardData?.id;
          const acceptableSets = hasSpecificId ? [] : (cardData?.setName ? [cardData.setName] : []);

          // Get min condition from defaults (get highest selected condition)
          const getMinCondition = () => {
            const conditions = wantListDefaults?.defaultConditions || ['NM', 'LP', 'MP'];
            const backendConditions = mapFrontendConditionsToBackend(conditions);
            // If MP is selected, use it as minimum (accepts NM, LP, MP)
            if (backendConditions.includes('MODERATELY_PLAYED')) return 'MODERATELY_PLAYED';
            // If only LP selected, use it as minimum (accepts NM, LP)
            if (backendConditions.includes('SLIGHTLY_PLAYED')) return 'SLIGHTLY_PLAYED';
            // If only NM selected
            return 'NEAR_MINT';
          };

          const requestPayload = {
            cardName: cardData?.name || 'Unknown Card',
            oracleId: identifier,
            quantity: 1, // Default to adding 1 copy
            acceptableSets: acceptableSets, // Empty when we have specific ID
            acceptableLanguages: wantListDefaults?.defaultLanguages
              ? mapFrontendLanguagesToBackend(wantListDefaults.defaultLanguages)
              : ['en'], // Fallback to English (lowercase for backend)
            minCondition: getMinCondition(),
            acceptFoil: wantListDefaults?.defaultAcceptFoil ?? true,
            acceptNonFoil: wantListDefaults?.defaultAcceptNonFoil ?? true,
            // Pass the specific card ID if available to ensure exact printing is added
            ...(cardData?.id && { acceptedPrintingIds: [cardData.id] }),
          };

          console.log('Sending addFromPrintings request:', requestPayload);
          console.log('Specifically requesting set:', acceptableSets.length > 0 ? acceptableSets[0] : 'All sets');

          await addFromPrintings(requestPayload).unwrap();
          console.log('Card added successfully with addFromPrintings');
        }
      } catch (error) {
        console.error('Failed to add card:', error);
      }
    } else if (typeof identifier === 'number') {
      console.error('Want list requires oracleId (string), but received card ID (number):', identifier);
    } else {
      console.error('Invalid identifier for want list:', identifier);
    }
  };

  const onCardRemove = (identifier: string | number) => {
    // For want list, we now use the unique record ID (number)
    if (typeof identifier === 'number') {
      removeCard({ id: identifier, ...orderBy });
    } else {
      console.error('Invalid identifier for want list remove:', identifier);
    }
  };

  const onWantCardQuantityChange = async (identifier: string | number, change: number) => {
    // For want list, we now use the unique record ID (number)
    console.log('onWantCardQuantityChange called:', { identifier, change, orderBy });
    
    if (typeof identifier === 'number') {
      try {
        const result = await wantListCardQuantityChange({ id: identifier, change, ...orderBy }).unwrap();
        console.log('Quantity update successful:', result);
      } catch (error) {
        console.error('Quantity update failed:', error);
      }
    } else {
      console.error('Invalid identifier type for want list quantity change:', typeof identifier);
    }
  };

  const onHeaderSortPress = (key: string, explicitDirection?: SortOrder) => {
    if (explicitDirection) {
      setOrderBy({ sort: key, direction: explicitDirection });
    } else if (key === orderBy.sort) {
      setOrderBy((prev) => ({
        sort: key,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      setOrderBy({
        direction: 'asc',
        sort: key,
      });
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleDeleteAll = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await deleteAllWantListItems().unwrap();
      toast.success(`Successfully deleted ${result.deletedCount} items from your want list`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete want list:', error);
      toast.error('Failed to delete want list. Please try again.');
    }
  };

  const handleBatchUpdate = useCallback(async (ids: number[], changes: { acceptableConditions?: string[]; acceptFoil?: boolean; acceptNonFoil?: boolean; acceptableLanguages?: string[] }) => {
    const result = await batchUpdate({ ids, changes }).unwrap();
    return result;
  }, [batchUpdate]);

  const handleViewModeChange = useCallback((mode: 'list' | 'comfortable' | 'compact') => {
    setViewMode(mode);
    // Save preference to localStorage
    localStorage.setItem('wantListViewMode', mode);
  }, []);

  // Calculate total pages based on 100 records per page
  const totalPages = wantListData ? Math.ceil(wantListData.totalRecords / 100) : 1;

  return (
    <div className="want-list">
      <MyCardsCollection
        isLoading={wantListLoading}
        data={allWantListCards}
        order={orderBy.direction}
        orderBy={orderBy.sort}
        addCardsPlaceholder={WANT_LIST_ADD_CARDS_PLACEHOLDER}
        addCardsTitle="Add cards to your Want List"
        addCardsSubtitle="Other traders can send you these cards at any time in exchange for your points."
        onHeaderSortPress={onHeaderSortPress}
        onCardAdd={onCardAdd}
        cardQuantityChange={onWantCardQuantityChange}
        onCardDelete={onCardRemove}
        useOracleId={true}
        isWantList={true}
        onDeleteAll={handleDeleteAll}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={wantListData?.totalCards || 0}
        totalRecords={wantListData?.totalRecords || 0}
        onPageChange={handlePageChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isSearchOpen={isSearchOpen}
        onSearchToggle={() => setIsSearchOpen(!isSearchOpen)}
        filteredCount={wantListData?.filteredCount}
        onBatchUpdateWantList={handleBatchUpdate}
      />
      <DeleteWantListModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
